import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Search, HelpCircle, Zap, MessageSquare, CreditCard, 
  ShieldCheck, ChevronDown, ChevronUp, Mail, Trash2,
  Calendar, Users
} from "lucide-react";

// Search Match Highlighter
const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safeQuery})`, 'gi'));
  return parts.map((part, index) => 
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={index} className="bg-blue-100 text-blue-700 px-0.5 rounded-[3px] font-bold">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const faqCategories = [
  { id: "all", label: "All Questions", icon: HelpCircle },
  { id: "getting-started", label: "Getting Started", icon: Zap },
  { id: "scanning", label: "Card Scanning", icon: Zap },
  { id: "whatsapp", label: "WhatsApp Messages", icon: MessageSquare },
  { id: "events", label: "Event Passes", icon: Calendar },
  { id: "pricing", label: "Plans & Billing", icon: CreditCard },
  { id: "team", label: "Team Sync", icon: Users },
  { id: "privacy", label: "Data & Account", icon: ShieldCheck },
];

const faqs = [
  // --- 1. Getting Started ---
  {
    category: "getting-started",
    question: "What is ExpoLead and how does it help at trade shows?",
    answer: "ExpoLead is an AI-powered exhibition lead capture platform designed for trade shows. Instead of accumulating paper visiting cards and manually entering data into spreadsheets after an event, booth staff snap a quick photo of a visiting card. The app extracts all contact parameters within 2 seconds and instantly broadcasts a branded digital introductory profile to the visitor's WhatsApp."
  },
  {
    category: "getting-started",
    question: "How do I get started with ExpoLead?",
    answer: "Getting started takes less than 2 minutes! Create a free account, complete your profile with your company name, designation, website, and specialization, connect your WhatsApp Business line, and you are ready to start capturing leads at your next exhibition."
  },
  {
    category: "getting-started",
    question: "Can multiple team members use ExpoLead at the same booth?",
    answer: "Yes! Your entire booth team can log into ExpoLead on their individual mobile phones. All business cards scanned by your team will automatically synchronize into your central leads database and Team Stats directory in real time."
  },
  {
    category: "getting-started",
    question: "Do I need dedicated card scanners or hardware devices?",
    answer: "No extra hardware or physical card scanners are required. ExpoLead operates directly on any smartphone or tablet camera (iPhone or Android) with real-time AI optical recognition."
  },

  // --- 2. Card Scanning & AI ---
  {
    category: "scanning",
    question: "How does the card scanner read business card details?",
    answer: "When you snap a photo of a card, our AI engine automatically detects orientation (horizontal or vertical), extracts text regions, and organizes details into structured fields: Full Name, Job Title, Company Name, Mobile Number, Secondary Phone, Email Address, and Location."
  },
  {
    category: "scanning",
    question: "How long does it take to process a business card?",
    answer: "Card processing takes just 1 to 2 seconds. The contact details appear on your screen immediately for quick review, tagging, and saving."
  },
  {
    category: "scanning",
    question: "Can I capture dual-sided visiting cards?",
    answer: "Yes. ExpoLead provides front and back capture slots. If the card contains company branches, landlines, or product catalogues on the reverse side, you can capture both sides into one complete lead record."
  },
  {
    category: "scanning",
    question: "What if a visiting card is poorly lit or crumpled?",
    answer: "The built-in camera engine automatically handles edge detection, contrast correction, and perspective de-skewing. You can also manually adjust or edit any text field before saving."
  },
  {
    category: "scanning",
    question: "Can I add smart tags, voice notes, and follow-up reminders?",
    answer: "Yes. After scanning, you can assign smart tags (like Hot Lead, VIP, Dealer, Follow Up), record audio voice notes summarizing conversations, and set timestamped reminders for scheduled client follow-ups."
  },

  // --- 3. WhatsApp Integration ---
  {
    category: "whatsapp",
    question: "How does automated WhatsApp broadcasting work?",
    answer: "When a lead with a valid mobile number is saved, ExpoLead queues a background task that dispatches an approved WhatsApp Business template message. The message introduces your company, specialization, website link, and direct contact details to the visitor."
  },
  {
    category: "whatsapp",
    question: "What is Meta Auto Connect in WhatsApp Settings?",
    answer: "Auto Connect uses Meta's Embedded Signup dialog. It connects your WhatsApp Business Account (WABA) and Phone Number ID to your ExpoLead account, enabling instant official message delivery without manual API token configurations."
  },
  {
    category: "whatsapp",
    question: "Do visitors need to save our contact number to receive the message?",
    answer: "No. Because messages are dispatched through Meta's WhatsApp Cloud Business API, they land directly inside the visitor's primary WhatsApp chat window even if they have not added your number to their address book."
  },
  {
    category: "whatsapp",
    question: "Can I send our digital product catalogue via WhatsApp?",
    answer: "Yes. In your Profile settings, add your PDF brochure or cloud catalogue URL in the Website field. The template dynamically includes this link so visitors can browse your products on their phones immediately."
  },

  // --- 4. Event Passes & Subscriptions ---
  {
    category: "events",
    question: "What are the subscription plans available?",
    answer: "We offer 4 flexible tiers: 1) Free Trial: 20 Card Scans + 20 WhatsApp Messages; 2) Event Lite (₹4,500/event): Up to 3,000 card scans, 1,000 WhatsApp conversations/day for 2-3 day expos; 3) Event Pro (₹6,500/event): Up to 10,000 card scans, 2,000 WhatsApp conversations/day for 4-5 day expos; 4) Business Annual (₹25,000/year): Unlimited card scans and messages across all global exhibitions for 365 days."
  },
  {
    category: "events",
    question: "Why is selecting an Exhibition/Event compulsory when buying a plan?",
    answer: "Event Lite and Event Pro passes are attached to the specific trade show you attend. Linking your pass to an event in our Global Events directory ensures your scan credits and WhatsApp quotas remain valid through the final day of the exhibition."
  },
  {
    category: "events",
    question: "What happens when an exhibition ends?",
    answer: "For Event Lite and Event Pro passes, the subscription remains active until 23:59:59 on the event's scheduled end date. After conclusion, your account switches to read-only export access so your captured leads and analytics remain downloadable."
  },

  // --- 5. Billing & GST Invoices ---
  {
    category: "pricing",
    question: "How do I get an official GST Tax Invoice for company reimbursement?",
    answer: "Every purchase generates a sequential GST Tax Invoice (e.g. NSINV-EL-0001). You can view, download, or print invoices with CGST (9%) and SGST (9%) breakdowns anytime in the Payment > Bill History section."
  },
  {
    category: "pricing",
    question: "How do I ensure my company's GSTIN appears on the bill?",
    answer: "Save your 15-digit GST number in your Profile settings under 'Buyer GSTIN', or enter it directly on the Checkout Summary screen before paying. It will automatically populate on your tax invoice."
  },
  {
    category: "pricing",
    question: "What are the seller GST credentials on the invoice?",
    answer: "Invoices are officially issued by EXPOLEAD, NSENGINEERS, 68-A, Pushpraj Complex, Vatva, Ahmedabad, Gujarat - 382445 (GSTIN: 24ALLPD3750G1ZD, SAC Code: 998313 - Information Technology Services)."
  },

  // --- 6. Team Sync & Export ---
  {
    category: "team",
    question: "How do I export leads to Excel or CRM platforms?",
    answer: "Tap the 'XLSX' or 'CSV' buttons on the Pipeline dashboard or All Leads screen. ExpoLead generates a formatted sheet containing names, companies, job titles, emails, phone numbers, addresses, tags, notes, and timestamps."
  },
  {
    category: "team",
    question: "Can I share a digital visiting card link with prospects?",
    answer: "Yes. Every captured lead generates a unique digital card link with single-tap vCard (.vcf) download capabilities, allowing prospects to save your contact information into their mobile address book."
  },

  // --- 7. Data Security & Privacy ---
  {
    category: "privacy",
    question: "Is our trade show lead data kept private and secure?",
    answer: "Yes. Your scanned contacts belong exclusively to your organization. Data is protected with encrypted database storage, secure JWT authentication, and strict tenant isolation. We never sell or share your contact records."
  },
  {
    category: "privacy",
    question: "How do I permanently delete my account and data?",
    answer: "You maintain full data autonomy. You can delete individual leads anytime or submit a permanent account deletion request via the 'Request Deletion' button in the Data Security section."
  }
];

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (val.trim() !== "") {
      setActiveCategory("all");
      setOpenFaqIndex(null); 
    }
  };

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#f0f4f9] font-sans text-slate-800 relative overflow-x-hidden flex flex-col justify-between selection:bg-blue-200">
      
      {/* AMBIENT BACKGROUND GLOW */}
      <div className="fixed top-0 left-1/4 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-400/10 rounded-full blur-[140px] pointer-events-none" />

      {/* MAIN CONTAINER */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 w-full flex-grow grid grid-cols-1 lg:grid-cols-4 gap-8 relative z-10">
        
        {/* LEFT FIXED NAVIGATION SIDEBAR */}
        <aside className="lg:col-span-1 space-y-6">
          <Link to="/" className="inline-block px-2">
            <span className="text-2xl font-black tracking-tight text-slate-900">
              Expo<span className="text-blue-600">Lead</span>
            </span>
          </Link>

          <nav className="flex flex-col gap-1.5 bg-white/80 backdrop-blur-md p-3 rounded-3xl border border-white shadow-sm">
            <div className="px-5 py-3 text-sm font-bold bg-blue-600 text-white rounded-2xl shadow-md shadow-blue-600/20">
              Help Center & FAQ
            </div>
            <Link
              to="/privacy-policy"
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-and-conditions"
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Terms of Service
            </Link>
            <Link
              to="/refund-policy"
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Refund Policy
            </Link>
            <Link
              to="/about"
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              About Us
            </Link>
          </nav>
        </aside>

        {/* RIGHT CONTENT AREA */}
        <main className="lg:col-span-3 space-y-6">
          
          {/* SEARCH HERO BANNER */}
          <div className="bg-[#0b132b] text-white rounded-[2rem] p-8 sm:p-10 space-y-4 relative overflow-hidden shadow-xl border border-slate-800">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Got Questions? Ask Us Anything 💬</h1>
            <p className="text-slate-300 text-sm max-w-xl font-normal leading-relaxed">
              Everything you need to know about AI card scanning, official WhatsApp introductions, mandatory event passes, GST tax invoicing, and team collaboration.
            </p>

            <div className="relative pt-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search questions (e.g. GST, Event Lite, WhatsApp, Export, Scans)..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-800/80 border border-slate-700 rounded-2xl text-sm font-medium text-white placeholder-slate-400 outline-none focus:border-blue-500 transition"
              />
            </div>
          </div>

          {/* FAQ CATEGORY PILLS */}
          <div className="flex gap-2.5 overflow-x-auto pb-2 hide-scrollbar">
            {faqCategories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setSearchQuery("");
                    setOpenFaqIndex(null);
                  }}
                  className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold transition whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-blue-600 text-white shadow-md shadow-blue-600/25"
                      : "bg-white text-slate-700 border border-slate-100 hover:bg-slate-50 shadow-xs"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* SPACIOUS ACCORDION FAQ CARDS (Matches Image 2) */}
          <div className="space-y-3.5">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const isAutoOpen = searchQuery.trim() !== "" && faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
                const isOpen = openFaqIndex === index || isAutoOpen;

                return (
                  <div 
                    key={index} 
                    className="bg-white rounded-2xl border border-slate-100/90 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:border-blue-200/60"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full text-left px-7 py-5 flex items-center justify-between font-bold text-slate-900 text-[15px] sm:text-base hover:text-blue-600 transition cursor-pointer"
                    >
                      <span className="pr-4 leading-snug">{highlightMatch(faq.question, searchQuery)}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-blue-600 shrink-0 transition-transform" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 transition-transform" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-7 pb-6 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4 font-normal">
                        {highlightMatch(faq.answer, searchQuery)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80 shadow-sm space-y-2">
                <HelpCircle className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="text-base font-bold text-slate-800">No matching questions found</p>
                <p className="text-xs font-medium text-slate-400">Try searching with other keywords or choose a category above.</p>
              </div>
            )}
          </div>

          {/* ACCOUNT DELETION BANNER (STRICTLY IN PRIVACY TAB) */}
          {activeCategory === "privacy" && (
            <div className="p-6 sm:p-8 bg-rose-50 border border-rose-100 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
              <div className="flex items-center gap-5 text-center sm:text-left flex-col sm:flex-row">
                <div className="w-14 h-14 bg-rose-200/50 rounded-2xl flex items-center justify-center shrink-0 text-rose-600 shadow-sm border border-rose-200">
                  <Trash2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 mb-1">Account & Lead Data Deletion</h3>
                  <p className="text-xs font-medium text-rose-900/70 max-w-md leading-relaxed">
                    Permanently delete your account credentials or erase all scanned visiting card records. This action cannot be reversed.
                  </p>
                </div>
              </div>
              <a 
                href="https://expolead.thensgroup.in/account-deletion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-rose-500/30 whitespace-nowrap active:scale-95 cursor-pointer"
              >
                <Trash2 className="w-4 h-4" /> Request Deletion
              </a>
            </div>
          )}

          {/* CONTACT SUPPORT CARD */}
          <div className="p-6 sm:p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
            <div className="space-y-1 text-center sm:text-left">
              <h3 className="font-bold text-lg">Have a specific question about your upcoming expo?</h3>
              <p className="text-xs text-blue-100">Our engineering and on-ground exhibition support team in Ahmedabad is available.</p>
            </div>
            <a
              href="mailto:expolead@thensgroup.in"
              className="px-5 py-3 bg-white text-blue-600 rounded-xl font-bold text-xs hover:bg-blue-50 transition shadow-md flex items-center gap-2 shrink-0 cursor-pointer"
            >
              <Mail className="w-4 h-4" /> expolead@thensgroup.in
            </a>
          </div>

        </main>
      </div>

      {/* FOOTER */}
      <footer className="py-6 text-center text-xs font-semibold text-slate-400 border-t border-slate-200/80 bg-white/50 backdrop-blur-md">
        <p>Copyright &copy; {new Date().getFullYear()} NS Engineers. All rights reserved.</p>
      </footer>
    </div>
  );
}